import React, {
 useEffect, useLayoutEffect, useRef, useState,
} from 'react';

/**
 * A totals row under a table, aligned to the table's own rendered columns.
 *
 * react-data-table-component has no footer, and appending a totals row to the data would let it
 * sort into the middle of the table. So this is a separate element — which leaves the problem of
 * making it line up.
 *
 * **It measures rather than mirrors.** The first version reimplemented RDT's cell maths from the
 * column definitions (`flex-grow` from `grow`, `min-width` from `minWidth`, matching padding) and
 * drifted, because that is not the whole of what RDT applies. Reading the header cells' real
 * widths is exact by construction and cannot fall out of step with a library detail, a sort, or a
 * future RDT version. `rdt_TableHeadRow` and `rdt_TableCol` are RDT's documented styling hooks,
 * not internals.
 *
 * Before the first measurement — and under jsdom, where layout does not run and every width is
 * zero — it falls back to the flex mirror, which is close enough for a test to assert content.
 *
 * **Scroll sync.** A wide table scrolls inside RDT's own wrapper, which this footer is a sibling
 * of, so without following that offset the totals slide out of line the moment a user scrolls.
 *
 * Give a column a `footer` function to fill its cell; columns without one stay blank, matching
 * fleetconsole, which totals only what is meaningful to add. Summing a peak speed or an idle
 * *share* is not, so those stay empty rather than showing a number nobody should read.
 */
const OdTableFooter = (props) => {
    const { columns, label, scrollerRef } = props;
    const trackRef = useRef(null);
    const [widths, setWidths] = useState([]);

    // Layout effect: measure before paint, so the footer never shows a frame at the wrong widths.
    useLayoutEffect(() => {
        const headRow = scrollerRef?.current?.querySelector('.rdt_TableHeadRow');
        if (!headRow) return undefined;

        const measure = () => {
            const next = Array.from(headRow.children, (cell) => cell.getBoundingClientRect().width);
            setWidths((current) => (
                current.length === next.length && current.every((w, i) => w === next[i])
                    ? current
                    : next
            ));
        };
        measure();

        // Column widths move when the card resizes, and RDT re-lays out on sort. Observing the
        // header row covers both without polling.
        const Observer = typeof ResizeObserver === 'function' ? ResizeObserver : null;
        const observer = Observer ? new Observer(measure) : null;
        if (observer) {
            observer.observe(headRow);
            Array.from(headRow.children).forEach((cell) => observer.observe(cell));
        }
        return () => observer?.disconnect();
    }, [scrollerRef, columns]);

    useEffect(() => {
        // RDT renders exactly one scrolling wrapper as the sole child of the container it is given.
        const scroller = scrollerRef?.current?.firstElementChild;
        const track = trackRef.current;
        if (!scroller || !track) return undefined;

        const sync = () => { track.scrollLeft = scroller.scrollLeft; };
        sync();
        scroller.addEventListener('scroll', sync, { passive: true });
        return () => scroller.removeEventListener('scroll', sync);
    }, [scrollerRef, columns]);

    const measured = widths.length === columns.length && widths.some((width) => width > 0);

    return (
        // No table roles: this sits outside the table element, so claiming row/cell would splice a
        // phantom row into the table's accessibility tree. A labelled group is what it actually is.
        <div className="od-table-foot" ref={trackRef} role="group" aria-label="Column totals">
            <div className="od-table-foot-row">
                {columns.map((column, index) => (
                    <div
                      // Columns are a static, ordered definition list — the index is their identity.
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      className={`od-table-foot-cell${column.right ? ' od-table-foot-right' : ''}`}
                      style={measured
                          ? { width: `${widths[index]}px`, flex: '0 0 auto' }
                          : { flex: `${column.grow || 0} 0 0`, minWidth: column.minWidth }}
                    >
                        {index === 0 ? label : (column.footer && column.footer())}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OdTableFooter;
